import { useMemo, useRef } from "react";

export interface StopwatchHandle {
  /** Resume or begin a segment — sets the segment start to now. Does not clear accumulated time. */
  start: () => void;
  /** Freeze the current segment, add its elapsed time to accumulated, and return the total so far. */
  stop: () => number;
  /** Begin a completely fresh session: clear accumulated time and start a new segment. */
  reset: () => void;
}

/**
 * Imperative stopwatch. start()/stop() are pause/resume; reset() starts a new session.
 * Returns a stable handle backed by refs — no re-renders on tick.
 */
export function useStopwatch(): StopwatchHandle {
  const startedAtRef = useRef<number | null>(null);
  const accumulatedRef = useRef(0);

  return useMemo(
    () => ({
      start() {
        startedAtRef.current = Date.now();
      },
      stop() {
        if (startedAtRef.current !== null) {
          accumulatedRef.current += Date.now() - startedAtRef.current;
          startedAtRef.current = null;
        }
        return accumulatedRef.current;
      },
      reset() {
        accumulatedRef.current = 0;
        startedAtRef.current = Date.now();
      },
    }),
    []
  );
}
